/**
 * Runtime configuration (requirement 1: configurable base path + snapshot root).
 *
 * `VITE_BASE_PATH` also feeds `vite.config.ts`'s `base` (build-time asset
 * URLs); it is re-read here so in-app hash routing can compute links
 * relative to the same sub-path without hardcoding it twice.
 *
 * `VITE_SNAPSHOT_ROOT` is the one thing that makes this a pure static
 * client: every data fetch in `src/data/` resolves against this root, never
 * against a same-origin API route. It may be a relative path (default,
 * "./snapshots/" — the Futarchists site drops published snapshots next to
 * the built bundle) or an absolute URL (a CDN/object-storage origin serving
 * the publisher's rsync target directly).
 */

const DEFAULT_SNAPSHOT_ROOT = "./snapshots/";
const DEFAULT_BASE_PATH = "/observatory/";

function normalizeRoot(root: string): string {
  return root.endsWith("/") ? root : `${root}/`;
}

/** Treats an unset *or empty-string* env var as "not provided" — Vite (and
 * `vi.stubEnv` in tests) can define a `VITE_*` var as `""` rather than
 * leaving it `undefined`, and an empty snapshot root/base path is never a
 * meaningful override. */
function readEnv(name: string): string | undefined {
  const value = import.meta.env[name] as string | undefined;
  return value === undefined || value === "" ? undefined : value;
}

export interface ObservatoryConfig {
  readonly basePath: string;
  readonly snapshotRoot: string;
  /** Default roles to probe for the per-organism screens when `fleet.json`
   * has not been published yet — `organismId` here is the real contract
   * directory name (verified live 2026-09-26,
   * e.g. "male-equity-founder-0001"), used to build fetch paths; `fleet.json`
   * (when present) is the authoritative source and overrides this. */
  readonly defaultRoles: readonly { role: string; organismId: string; displayName: string }[];
  readonly pollIntervalMs: number;
  readonly pollBackoffMaxMs: number;
}

export const config: ObservatoryConfig = {
  basePath: readEnv("VITE_BASE_PATH") ?? DEFAULT_BASE_PATH,
  snapshotRoot: normalizeRoot(readEnv("VITE_SNAPSHOT_ROOT") ?? DEFAULT_SNAPSHOT_ROOT),
  defaultRoles: [
    {
      role: "male_equity",
      organismId: "male-equity-founder-0001",
      displayName: roleLabel("male_equity"),
    },
    {
      role: "female_crypto",
      organismId: "female-crypto-founder-0001",
      displayName: roleLabel("female_crypto"),
    },
  ],
  // Owner requirement 2026-09-26, requirement 5: "poll cadence = min(role
  // cadence, 15s)" — the female's cadence is 30s, the male's is 60s, so 15s
  // is the binding minimum for both.
  pollIntervalMs: 15_000,
  pollBackoffMaxMs: 60_000,
};

/** Friendly label for a short role name — used by the fleet bar/screen
 * (owner requirement 2026-09-26: "show a friendly label from role
 * (male_equity → 'Male · equities', female_crypto → 'Female · crypto')"). */
export function roleLabel(role: string): string {
  switch (role) {
    case "male_equity":
      return "Male · equities";
    case "female_crypto":
      return "Female · crypto";
    default:
      return role;
  }
}

export function snapshotUrl(path: string): string {
  if (/^https?:\/\//.test(config.snapshotRoot)) {
    return new URL(path, config.snapshotRoot).toString();
  }
  return `${config.snapshotRoot}${path}`;
}
